/** Thin REST helpers for the room lifecycle + local seat-token persistence. */
import type { JoinResult, RoomMode } from "../shared/protocol";
import type { ReplayRecord, ReplaySummary } from "../shared/replay";
import type { PlayerId } from "../core";
import { authToken } from "./account";

export interface StoredSeat {
  readonly seat: PlayerId;
  readonly token: string;
}

async function postJoin(url: string, body: { name?: string; mode?: RoomMode } = {}): Promise<JoinResult> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `Request failed (${res.status})`);
  }
  return res.json();
}

/** Create a room; `mode` (set by the host) fixes draft vs classic for both players. */
export function createRoom(name?: string, mode?: RoomMode): Promise<JoinResult> {
  return postJoin("/api/rooms", { name, mode });
}

export function joinRoom(roomId: string, name?: string): Promise<JoinResult> {
  return postJoin(`/api/rooms/${roomId}/join`, { name });
}

const seatKey = (roomId: string) => `fn-room-${roomId}`;

export function storeSeat(roomId: string, result: JoinResult): void {
  localStorage.setItem(seatKey(roomId), JSON.stringify({ seat: result.seat, token: result.token }));
}

export function loadSeat(roomId: string): StoredSeat | null {
  const raw = localStorage.getItem(seatKey(roomId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredSeat;
  } catch {
    return null;
  }
}

export function roomLink(roomId: string): string {
  return `${location.origin}/g/${roomId}`;
}

// --- ranked matchmaking ------------------------------------------------------

export interface MatchStatus {
  readonly status: "queued" | "matched" | "idle";
  readonly roomId?: string;
  readonly seat?: PlayerId;
  readonly token?: string;
  readonly opponent?: string;
  readonly waiting?: number;
}

function authHeaders(): Record<string, string> {
  const t = authToken();
  const h: Record<string, string> = { "content-type": "application/json" };
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

async function matchmaking(op: "join" | "poll" | "cancel"): Promise<MatchStatus> {
  const res = await fetch(`/api/matchmaking/${op}`, { method: "POST", headers: authHeaders() });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Matchmaking failed (${res.status})`);
  }
  return res.json();
}

export const matchmakingJoin = (): Promise<MatchStatus> => matchmaking("join");
export const matchmakingPoll = (): Promise<MatchStatus> => matchmaking("poll");
export const matchmakingCancel = (): Promise<void> => {
  return matchmaking("cancel").then(
    () => undefined,
    () => undefined,
  );
};

// --- replays / game history ----------------------------------------------------

export async function fetchReplay(id: string): Promise<ReplayRecord> {
  const res = await fetch(`/api/replays/${encodeURIComponent(id)}`);
  const body = (await res.json().catch(() => ({}))) as { replay?: ReplayRecord; error?: string };
  if (!res.ok || !body.replay) throw new Error(body.error ?? "Could not load the game.");
  return body.replay;
}

/** The signed-in account's recent games; empty when signed out or offline. */
export async function fetchMyReplays(): Promise<ReplaySummary[]> {
  const t = authToken();
  if (!t) return [];
  try {
    const res = await fetch("/api/replays", { headers: { Authorization: `Bearer ${t}` } });
    if (!res.ok) return [];
    const data = (await res.json()) as { replays: ReplaySummary[] };
    return data.replays ?? [];
  } catch {
    return [];
  }
}

// --- leaderboard -------------------------------------------------------------

export interface LeaderRow {
  readonly id: string;
  readonly username: string;
  readonly rating: number;
  readonly wins: number;
  readonly losses: number;
}

export async function fetchLeaderboard(): Promise<LeaderRow[]> {
  try {
    const res = await fetch("/api/leaderboard");
    if (!res.ok) return [];
    const data = (await res.json()) as { leaderboard: LeaderRow[] };
    return data.leaderboard ?? [];
  } catch {
    return [];
  }
}
